/**
 * What a day worked is, and what it costs.
 *
 * Two halves that behave differently on purpose, and the screen has to make the difference
 * obvious. The settings at the top are ordinary numbers, edited in place and saved together,
 * which is why the save bar belongs to that section alone. The rate below is a **new version
 * every time**: a rate raised in March must not reprice February, so there is deliberately no
 * way to edit one from here — no pencil, no inline field, nothing that could be mistaken for
 * correcting a typo. Somebody who could would silently reprice a month that has already been
 * settled and argued over.
 *
 * The history is on the screen for the same reason. A settled month has to remain explainable
 * next year, and the only way to explain it is to be able to see what the rate was at the
 * time.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  daysOfWeek,
  dutyLimits,
  reportsApi,
  type DutyRate,
  type DutySettings,
} from '../../reports/api';
import { useTenantFormats, type TenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  FieldErrors,
  Input,
  Measured,
  PageHeader,
  Pill,
  SaveBar,
  Section,
  Select,
  SettingRow,
  Switch,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';
import { driversApi, type FleetDriver } from '../drivers';
import { describe, refusal } from '../errors';
import { checkForm, named } from '../check';

/**
 * The settings this page saves as one thing. The save bar counts them, so the list is here
 * rather than implied by whichever rows happen to be drawn — currency and timezone come back
 * from the server read-only and must never be counted as a change.
 */
const EDITED: (keyof DutySettings)[] = [
  'dutyMinimumMinutes',
  'weekStartsOn',
  'silentShiftTimeoutMinutes',
  'maxShiftHours',
  'driversSeeTheirDues',
  'driversSeeTheirTakings',
];

export function DutyPage() {
  const { t } = useTranslation();
  // What `checkForm` reads before a save: every control on the page, at once (#377).
  const page = useRef<HTMLDivElement>(null);
  const formats = useTenantFormats();

  const [loaded, setLoaded] = useState<DutySettings | null>(null);
  const [draft, setDraft] = useState<DutySettings | null>(null);
  const [rates, setRates] = useState<DutyRate[]>([]);
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState<FieldProblem | null>(null);
  const [rateProblem, setRateProblem] = useState<string | null>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const [introducing, setIntroducing] = useState(false);

  const load = useCallback(async () => {
    setProblem(null);

    try {
      const [duty, versions, fleet] = await Promise.all([
        reportsApi.duty(),
        reportsApi.rates(),
        driversApi.list(),
      ]);

      setLoaded(duty);
      setDraft(duty);
      setRates(versions);
      setDrivers(fleet);
    } catch (error) {
      setProblem(refusal(error, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  /** The confirmation is a strip that goes away by itself; nobody dismisses "Saved". */
  useEffect(() => {
    if (!saved) return undefined;

    const timer = setTimeout(() => setSaved(false), 4000);

    return () => clearTimeout(timer);
  }, [saved]);

  const names = useMemo(
    () => new Map(drivers.map((driver) => [driver.driverId, driver.displayName])),
    [drivers],
  );

  const changes =
    draft === null || loaded === null
      ? 0
      : EDITED.filter((key) => draft[key] !== loaded[key]).length;

  if (draft === null || loaded === null) {
    return (
      <>
        <PageHeader
          title={t('nav.settings.duty')}
          description={t('nav.settingsAbout.duty')}
        />
        {problem === null ? (
          <EmptyState>{t('settings.loading')}</EmptyState>
        ) : (
          <Banner tone="danger">{problem.message}</Banner>
        )}
      </>
    );
  }

  const change = <K extends keyof DutySettings>(key: K, value: DutySettings[K]) => {
    setDraft({ ...draft, [key]: value });
    setSaved(false);
  };

  async function save() {
    if (draft === null) return;

    const invalid = checkForm(page.current, t);

    if (invalid !== null) {
      setProblem(invalid);
      return;
    }

    setSaving(true);
    setProblem(null);

    try {
      // Through the tenant store, which is what evicts the five-minute cache. Anything
      // else takes effect somewhere between now and five minutes from now, which is the
      // shape of bug that makes a working fix look broken.
      const settings = await reportsApi.saveDuty(draft);

      setLoaded(settings);
      setDraft(settings);
      setSaved(true);
    } catch (error) {
      setProblem(named(refusal(error, t, 'settings.couldNotSave'), page.current, t));
    } finally {
      setSaving(false);
    }
  }

  async function end(rate: DutyRate) {
    setEnding(rate.id);
    setRateProblem(null);

    try {
      await reportsApi.endRate(rate.id, new Date().toISOString());
      await load();
    } catch (error) {
      setRateProblem(describe(error, t, 'settings.couldNotSave'));
    } finally {
      setEnding(null);
    }
  }

  return (
    <FieldErrors root={page} problem={problem} onEdit={() => setProblem(null)}>
      <PageHeader title={t('nav.settings.duty')} description={t('nav.settingsAbout.duty')} />

      {/* The settings half is one form and one save, as it was: Enter in a number still
          saves the lot. The rates below are outside it on purpose — nothing about a rate is
          ever saved by this button. */}
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Section title={t('duty.title')} description={t('duty.intro')}>
          {saved && (
            <div className="px-5 pt-3">
              <Banner tone="ok">{t('settings.saved')}</Banner>
            </div>
          )}

          {problem !== null && (
            <div className="px-5 pt-3">
              <Banner tone="danger">{problem.message}</Banner>
            </div>
          )}

          <SettingRow label={t('duty.minimumMinutes')} help={t('duty.minimumMinutesHelp')}>
            <Measured
              id="dutyMinimumMinutes"
              type="number"
              aria-label={t('duty.minimumMinutes')}
              unit={t('settings.unitMinutes')}
              min={dutyLimits.dutyMinimumMinutes.min}
              max={dutyLimits.dutyMinimumMinutes.max}
              value={draft.dutyMinimumMinutes}
              onChange={(e) => change('dutyMinimumMinutes', Number(e.target.value))}
            />
          </SettingRow>

          <SettingRow label={t('duty.weekStartsOn')} help={t('duty.weekStartsOnHelp')}>
            <Select
              id="weekStartsOn"
              aria-label={t('duty.weekStartsOn')}
              value={draft.weekStartsOn}
              onChange={(e) =>
                change('weekStartsOn', e.target.value as DutySettings['weekStartsOn'])
              }
            >
              {daysOfWeek.map((day) => (
                <option key={day} value={day}>
                  {t(`duty.days.${day}`)}
                </option>
              ))}
            </Select>
          </SettingRow>

          <SettingRow label={t('duty.silenceTimeout')} help={t('duty.silenceTimeoutHelp')}>
            <Measured
              id="silentShiftTimeoutMinutes"
              type="number"
              aria-label={t('duty.silenceTimeout')}
              unit={t('settings.unitMinutes')}
              min={dutyLimits.silentShiftTimeoutMinutes.min}
              max={dutyLimits.silentShiftTimeoutMinutes.max}
              value={draft.silentShiftTimeoutMinutes}
              onChange={(e) => change('silentShiftTimeoutMinutes', Number(e.target.value))}
            />
          </SettingRow>

          <SettingRow label={t('duty.maxShiftHours')} help={t('duty.maxShiftHoursHelp')}>
            <Measured
              id="maxShiftHours"
              type="number"
              aria-label={t('duty.maxShiftHours')}
              unit={t('duty.unitHours')}
              min={dutyLimits.maxShiftHours.min}
              max={dutyLimits.maxShiftHours.max}
              value={draft.maxShiftHours}
              onChange={(e) => change('maxShiftHours', Number(e.target.value))}
            />
          </SettingRow>

          <SettingRow
            label={t('duty.driversSeeTheirDues')}
            help={t('duty.driversSeeTheirDuesHelp')}
          >
            <Switch
              checked={draft.driversSeeTheirDues}
              label={t('duty.driversSeeTheirDues')}
              onChange={(next) => change('driversSeeTheirDues', next)}
            />
          </SettingRow>

          {/* Beside the dues because it rides on them: with the dues off the phone is
              refused the whole response, so this one would change nothing (#410). */}
          <SettingRow
            label={t('duty.driversSeeTheirTakings')}
            help={t('duty.driversSeeTheirTakingsHelp')}
            last
          >
            <Switch
              checked={draft.driversSeeTheirTakings}
              disabled={!draft.driversSeeTheirDues}
              label={t('duty.driversSeeTheirTakings')}
              onChange={(next) => change('driversSeeTheirTakings', next)}
            />
          </SettingRow>
        </Section>
      </form>

      <Section
        title={t('duty.history')}
        description={t('duty.historyHelp')}
        actions={
          <Button size="sm" onClick={() => setIntroducing(true)}>
            {t('duty.introduce')}
          </Button>
        }
      >
        {rateProblem !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{rateProblem}</Banner>
          </div>
        )}

        {rates.length === 0 ? (
          <EmptyState>{t('duty.none')}</EmptyState>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th scope="col">{t('duty.appliesTo')}</Th>
                <Th scope="col" numeric>
                  {t('duty.dailyAmount')}
                </Th>
                <Th scope="col" numeric>
                  {t('duty.weeklyCap')}
                </Th>
                <Th scope="col">{t('duty.effectiveFrom')}</Th>
                <Th scope="col">{t('duty.effectiveTo')}</Th>
                <Th scope="col" />
              </Tr>
            </thead>
            <tbody>
              {rates.map((rate) => (
                <Tr key={rate.id}>
                  <Td>
                    {rate.driverId === null
                      ? t('duty.everybody')
                      : (names.get(rate.driverId) ?? rate.driverId)}
                  </Td>
                  <Td numeric>{amount(formats, rate.dailyAmount, rate.currency)}</Td>
                  <Td numeric>
                    {rate.weeklyCap === null ? '—' : amount(formats, rate.weeklyCap, rate.currency)}
                  </Td>
                  {/* The starter rate begins at the Unix epoch — "always", not a date. */}
                  <Td>
                    {Date.parse(rate.effectiveFrom) <= 0
                      ? t('duty.fromTheStart')
                      : formats.moment(rate.effectiveFrom)}
                  </Td>
                  <Td>
                    {rate.effectiveTo === null ? (
                      <Pill tone="ok" soft>
                        {t('duty.inForce')}
                      </Pill>
                    ) : (
                      formats.moment(rate.effectiveTo)
                    )}
                  </Td>
                  <Td numeric>
                    {rate.driverId !== null && rate.effectiveTo === null && (
                      <Button
                        size="sm"
                        disabled={ending === rate.id}
                        onClick={() => void end(rate)}
                      >
                        {t('duty.endDeal')}
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {/* Outside the form, and last: the bar is `sticky bottom-0`, so it can only pin to the
          foot of the page if nothing the page renders comes after it. Inside the form it
          wedged between the two cards. Enter in a number box still saves — the form keeps its
          `onSubmit` — and this button calls the same `save()`. */}
      <SaveBar
        changes={changes}
        problem={problem?.message}
        busy={saving}
        onDiscard={() => {
          setDraft(loaded);
          setSaved(false);
          setProblem(null);
        }}
        onSave={() => void save()}
        discardLabel={t('settings.discard')}
        saveLabel={saving ? t('settings.saving') : t('settings.save')}
      >
        {t('settings.unsaved', { count: changes })}
      </SaveBar>

      <RateDialog
        open={introducing}
        currency={draft.currency}
        drivers={drivers}
        onClose={() => setIntroducing(false)}
        onIntroduced={() => {
          setIntroducing(false);
          void load();
        }}
      />
    </FieldErrors>
  );
}

/**
 * A rate in the firm's own currency where it is one, and the amount beside its own code where
 * it is not — a rate introduced under a currency the firm has since changed still has to read
 * as the money it actually was.
 */
function amount(formats: TenantFormats, value: number, currency: string): string {
  return currency === formats.currency
    ? formats.money(value)
    : `${formats.amount(value)} ${currency}`;
}

/**
 * A new rate, from a date.
 *
 * Adds a version and never edits one. The dialog says so, because the alternative is somebody
 * correcting a typo and repricing a settled month without anything on the screen having
 * warned them.
 *
 * It stays mounted while the page is open, so a rate refused by the server is still in the
 * form when the dialog is reopened: the dialog closes on success and only on success.
 */
function RateDialog({
  open,
  currency,
  drivers,
  onClose,
  onIntroduced,
}: {
  open: boolean;
  currency: string;
  drivers: FleetDriver[];
  onClose: () => void;
  onIntroduced: () => void;
}) {
  const { t } = useTranslation();

  const [driverId, setDriverId] = useState('');
  const [dailyAmount, setDailyAmount] = useState('30');
  const [weeklyCap, setWeeklyCap] = useState('');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<FieldProblem | null>(null);
  const form = useRef<HTMLFormElement>(null);

  async function introduce() {
    const invalid = checkForm(form.current, t);

    if (invalid !== null) {
      setProblem(invalid);
      return;
    }

    setSaving(true);
    setProblem(null);

    try {
      await reportsApi.introduceRate({
        driverId: driverId === '' ? null : driverId,
        dailyAmount: Number(dailyAmount),
        weeklyCap: weeklyCap === '' ? null : Number(weeklyCap),
        currency,

        // Midnight local to whoever is typing, sent as an instant. The server stores it
        // as UTC and resolves a day's rate from the instant that day began, so a firm
        // saying "from the first of March" gets the first of March.
        effectiveFrom: new Date(`${from}T00:00:00`).toISOString(),
      });

      onIntroduced();
    } catch (error) {
      setProblem(named(refusal(error, t, 'settings.couldNotSave'), form.current, t));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title={t('duty.introduce')}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose} disabled={saving}>
            {t('duty.cancel')}
          </Button>
          <Button variant="primary" type="submit" form="duty-rate" disabled={saving}>
            {saving ? t('settings.saving') : t('duty.introduceAction')}
          </Button>
        </>
      }
    >
      <FieldErrors problem={problem} onEdit={() => setProblem(null)}>
      <form
        ref={form}
        id="duty-rate"
        noValidate
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          void introduce();
        }}
      >
        <p className="text-secondary text-ink-3 m-0">{t('duty.introduceHelp')}</p>

        {problem !== null && <Banner tone="danger">{problem.message}</Banner>}

        <Field id="driverId" label={t('duty.appliesTo')} help={t('duty.appliesToHelp')}>
          <Select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">{t('duty.everybody')}</option>
            {drivers.map((driver) => (
              <option key={driver.driverId} value={driver.driverId}>
                {driver.displayName}
              </option>
            ))}
          </Select>
        </Field>

        <Field id="dailyAmount" label={t('duty.dailyAmount')} help={t('duty.dailyAmountHelp', { currency })}>
          <Input
            type="number"
            step="0.01"
            required
            min="0"
            value={dailyAmount}
            onChange={(e) => setDailyAmount(e.target.value)}
          />
        </Field>

        <Field id="weeklyCap" label={t('duty.weeklyCap')} help={t('duty.weeklyCapHelp', { currency })}>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={weeklyCap}
            placeholder={t('duty.noCap')}
            onChange={(e) => setWeeklyCap(e.target.value)}
          />
        </Field>

        <Field id="effectiveFrom" label={t('duty.effectiveFrom')} help={t('duty.effectiveFromHelp')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
      </form>
      </FieldErrors>
    </Dialog>
  );
}
